import {ArrowNarrowLeftIcon} from '@heroicons/react/outline';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import Spinner from '@/assets/svg/Spinner';
import {Header} from '@/components';
import KycReceipt from '@/screens/KYC/Receipt';
import {kycService} from '@/services/kycService';
import type {AdminAfriexPerson} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

const KycReceiptPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const userId = params?.userId || '';

  const [kycDetails, setKycDetails] = useState<AdminAfriexPerson>(
    {} as AdminAfriexPerson,
  );

  const [kycLoading, setKycLoading] = useState(false);

  const fetchKYCDetails = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        throw new Error('User Id is required');
      }
      setKycLoading(true);
      const userResponse = await kycService.getKYCDetails(userId);
      setKycDetails(userResponse);
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      showErrorMessage('Failed to fetch KYC details:' + errorMessage);
    } finally {
      setKycLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // Fire-and-forget: fetchKYCDetails try/catches, clears `kycLoading` and
    // reports through showErrorMessage, so this only stops an unexpected
    // throw becoming an unhandled rejection.
    fetchKYCDetails().catch(() => {});
  }, [fetchKYCDetails]);

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header
        title="Kyc"
        icon={
          <ArrowNarrowLeftIcon
            className="font-light w-5 h-5 text-black dark:text-gray-200 hover:text-indigo-500 cursor-pointer"
            onClick={() => navigate(-1)}
          />
        }
      />

      <div className="flex justify-center items-center flex-col">
        {kycLoading ? (
          <Spinner size={8} className="text-cyan-600" />
        ) : (
          <KycReceipt data={kycDetails} />
        )}
      </div>
    </div>
  );
};

export default KycReceiptPage;
